<script setup>
import EnvelopeIcon from '@iconify-vue/ph/envelope';
import EyeIcon from '@iconify-vue/ph/eye';
import SendEmailAction from '@js/components/actions/SendEmailAction.vue';
import ActionButton from '@js/components/buttons/ActionButton.vue';
import DataTableLayout from '@js/components/tables/DataTableLayout.vue';
import BaseCell from '@js/components/tables/cells/BaseCell.vue';
import HeaderCell from '@js/components/tables/cells/HeaderCell.vue';
import NumberCell from '@js/components/tables/cells/NumberCell.vue';
import TextCell from '@js/components/tables/cells/TextCell.vue';
import { useDataTable } from '@js/utils/dataTable';
import { useRoute } from '@js/utils/route';
import { toastSuccess } from '@js/utils/toast';
import { createColumnHelper } from '@tanstack/vue-table';
import { h } from 'vue';

const { route } = useRoute();

const columnHelper = createColumnHelper();

const {
    loading,
    table,
    count,
    globalFilter,
    setGlobalFilter,
    availableFilters,
    filters,
    setFilters,
    updateData,
} = useDataTable({
    columns: [
        columnHelper.accessor(() => null, {
            id: 'name',
            headerCustom: true,
            header: (props) =>
                h(HeaderCell, {
                    header: props.header,
                    title: 'Nom',
                }),
            cell: (props) =>
                h(TextCell, {
                    text: props.row.original.name,
                }),
        }),
        columnHelper.accessor(() => null, {
            id: 'lastLoginDate',
            headerCustom: true,
            header: (props) =>
                h(HeaderCell, {
                    header: props.header,
                    title: 'Dernière<br/>connexion',
                }),
            cell: (props) =>
                props.row.original.lastLoginDate
                    ? h(TextCell, {
                          text: props.row.original.lastLoginDate,
                      })
                    : null,
        }),
        columnHelper.accessor(() => null, {
            id: 'numberOfTicketsToBeProcessed',
            headerCustom: true,
            header: (props) =>
                h(HeaderCell, {
                    header: props.header,
                    title: 'NB tâches<br/>À traiter',
                }),
            cell: (props) =>
                h(NumberCell, {
                    number: props.row.original.numberOfTicketsToBeProcessed,
                }),
            enableSorting: false,
        }),
        columnHelper.accessor(() => null, {
            id: 'maxTicketAnswerCreatedAt',
            headerCustom: true,
            header: (props) =>
                h(HeaderCell, {
                    header: props.header,
                    title: 'Dernière<br/>Réponse le',
                }),
            cell: (props) =>
                props.row.original.maxTicketAnswerCreatedAt
                    ? h(TextCell, {
                          text: props.row.original.maxTicketAnswerCreatedAt,
                      })
                    : null,
            enableSorting: false,
        }),
        columnHelper.accessor(() => null, {
            id: 'taskReminderEmailSentAt',
            headerCustom: true,
            header: (props) =>
                h(HeaderCell, {
                    header: props.header,
                    title: 'Email rappel<br/>Envoyé le',
                }),
            cell: (props) =>
                props.row.original.taskReminderEmailSentAt
                    ? h(TextCell, {
                          text: props.row.original.taskReminderEmailSentAt,
                      })
                    : null,
        }),
        columnHelper.display({
            id: 'actions',
            headerCustom: true,
            header: (props) =>
                h(HeaderCell, {
                    header: props.header,
                    title: 'Actions',
                    width: 0,
                }),
            cell: (props) =>
                h(
                    BaseCell,
                    {},
                    {
                        default: () => [
                            h(ActionButton, {
                                square: true,
                                size: 'small',
                                title: 'Voir',
                                type: 'secondary',
                                href: route(
                                    'front.projectManagement.task.tickets',
                                    {
                                        tenant: props.row.original.id,
                                    },
                                ),
                                icon: EyeIcon,
                            }),
                            h(
                                SendEmailAction,
                                {
                                    fromOptions:
                                        props.row.original.email.fromOptions,
                                    toOptions:
                                        props.row.original.email.toOptions,
                                    to: props.row.original.email.toOptions,
                                    subject: props.row.original.email.subject,
                                    body: props.row.original.email.body,
                                    callback: (
                                        from,
                                        to,
                                        subject,
                                        body,
                                        setLoading,
                                        setIsOpen,
                                    ) => {
                                        axios
                                            .post(
                                                route(
                                                    'front.projectManagement.task.postSendTicketEmailReminder',
                                                    {
                                                        tenant: props.row
                                                            .original.id,
                                                    },
                                                ),
                                                {
                                                    from: from,
                                                    to: to,
                                                    subject: subject,
                                                    body: body,
                                                },
                                            )
                                            .then(() => {
                                                setLoading(false);
                                                setIsOpen(false);

                                                toastSuccess(
                                                    'Envoyé avec succès.',
                                                );

                                                updateData();
                                            })
                                            .catch((error) => {
                                                console.error(error);
                                            });
                                    },
                                },
                                {
                                    trigger: () =>
                                        h(ActionButton, {
                                            square: true,
                                            size: 'small',
                                            title: 'Envoyer un rappel par email',
                                            icon: EnvelopeIcon,
                                            type: 'primary',
                                        }),
                                },
                            ),
                        ],
                    },
                ),
        }),
    ],
    url: route('front.projectManagement.task.postTickets'),
    columnPinning: {
        right: ['actions'],
    },
});
</script>

<template>
    <DataTableLayout
        :table="table"
        :count="count"
        :global-filter="globalFilter"
        :set-global-filter="setGlobalFilter"
        :available-filters="availableFilters"
        :filters="filters"
        :set-filters="setFilters"
        :loading="loading"
    />
</template>
